import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useServiceList } from './hooks'
import {
  Button,
  DatePicker,
  Flex,
  Input,
  notification,
  Table,
  Tag,
  Typography,
} from 'antd'
import { CButton, CCardBody } from '@coreui/react-pro'
import { ColumnsType } from 'antd/lib/table'
import { path } from '../../../../libs'
import CIcon from '@coreui/icons-react'
import { cilSync, cilZoom } from '@coreui/icons'
import { ServiceFilters, ServiceListWithStatus, ServiceNew } from './types'
import {
  addedDataToLanguages,
  serviceSyncMounted,
  syncAllLanguage,
  syncAllLanguageMounted,
} from './model'
import { useNavigate } from 'react-router-dom'
import { NotificationPlacement } from 'antd/es/notification/interface'
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import dayjs from 'dayjs'
import { useUrlFilters } from '../../../../libs/useUrlFilters'

const { Text } = Typography
const { RangePicker } = DatePicker

// ── Filter config ──────────────────────────────────────────
const FILTER_CONFIG = {
  service: { type: 'string' as const },
  updatedAt: { type: 'array' as const },
}

const getLanguagesByServices = (services: ServiceNew[]) => {
  const result: { [key: string]: string[] } = {}

  services.forEach((service) => {
    if (!Object.keys(result).includes(service.service)) {
      result[service.service] = []
    }
  })

  services.forEach((service) => {
    if (Object.keys(result).includes(service.service)) {
      result[service.service] = [...service.lang]
    }
  })

  return result
}

export const ServicesList: React.FC = () => {
  const navigate = useNavigate()

  const [api, contextHolder] = notification.useNotification()

  const { serviceList, onChangeServiceList, loading } = useServiceList()

  const { filters, setFilters, resetFilters } = useUrlFilters(FILTER_CONFIG)

  const [orderBy, setOrderBy] = useState('service')
  const [orderDirection, setOrderDirection] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Local input state — applied to URL only on Enter/blur
  const [inputService, setInputService] = useState(filters.service || '')

  const openSuccessNotification = (
    description: string,
    placement: NotificationPlacement,
  ) => {
    api.success({
      message: 'Sync all languages',
      description: description,
      placement,
    })
  }

  const openErrorNotification = (
    description: string,
    placement: NotificationPlacement,
  ) => {
    api.error({
      message: 'Error',
      description: description,
      placement,
    })
  }

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }
  }

  // ── Apply text search (service name) ────────────────────
  const applyServiceSearch = (value: string) => {
    const result = value.trim()
    setFilters({ service: result || undefined })
    setPage(1)
  }

  const clearServiceSearch = () => {
    setInputService('')
    setFilters({ service: undefined })
    setPage(1)
  }

  // ── Build API filters from URL state ────────────────────
  const buildApiFilters = useCallback((): ServiceFilters | undefined => {
    const apiFilters: ServiceFilters = {}
    if (filters.service) apiFilters.service = filters.service
    if (filters.updatedAt) apiFilters.updatedAt = filters.updatedAt
    return Object.keys(apiFilters).length > 0 ? apiFilters : undefined
  }, [filters])

  // ── Date range from URL ─────────────────────────────────
  const dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null =
    filters.updatedAt && filters.updatedAt[0] && filters.updatedAt[1]
      ? [dayjs(filters.updatedAt[0]), dayjs(filters.updatedAt[1])]
      : null

  // ── Active filter tags ───────────────────────────────────
  const activeTags = useMemo(() => {
    const tags: { key: string; label: string; clear: () => void }[] = []
    if (filters.service)
      tags.push({
        key: 'service',
        label: `Service: ${filters.service}`,
        clear: clearServiceSearch,
      })
    if (filters.updatedAt && filters.updatedAt[0] && filters.updatedAt[1])
      tags.push({
        key: 'date',
        label: `${filters.updatedAt[0]} — ${filters.updatedAt[1]}`,
        clear: () => {
          setFilters({ updatedAt: undefined })
          setPage(1)
        },
      })
    return tags
  }, [filters])

  useEffect(() => {
    const unsubscribeSuccess = syncAllLanguage.doneData.watch((data) =>
      openSuccessNotification(data.body.status, 'topRight'),
    )

    const unsubscribeError = syncAllLanguage.failData.watch((data) =>
      openErrorNotification(data.body.message, 'topRight'),
    )

    return () => {
      unsubscribeSuccess()
      unsubscribeError()
    }
  }, [])

  useEffect(() => {
    addedDataToLanguages(getLanguagesByServices(serviceList))
  }, [serviceList])

  useEffect(() => {
    onChangeServiceList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      filters: buildApiFilters(),
    })
  }, [pageSize, page, orderBy, orderDirection, filters])

  const columns: ColumnsType<ServiceNew> = [
    {
      title: 'Service',
      dataIndex: 'service',
      defaultSortOrder: 'ascend',
      sorter: true,
      render: (v: string) => (
        <Highlighter
          searchWords={[inputService.trim()]}
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          autoEscape
          textToHighlight={v ?? ''}
        />
      ),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      render: (value: string) => dayjs(value).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 150,
      render: (item: ServiceNew) => {
        return (
          <>
            <CButton
              color="secondary"
              className="me-2"
              title="View"
              onClick={() =>
                navigate(path.translations.services.details(item.service))
              }
            >
              <CIcon icon={cilZoom} />
            </CButton>
          </>
        )
      },
    },
    {
      title: 'Sync all languages',
      fixed: 'right',
      width: 150,
      render: (item: ServiceListWithStatus) => {
        return (
          <>
            <CButton
              color="info"
              className="me-2"
              title="Sync all languages"
              onClick={() => {
                if (window.confirm('Are you sure?')) {
                  syncAllLanguageMounted({
                    service: item.service,
                  })
                }
              }}
            >
              <CIcon icon={cilSync} />
            </CButton>
          </>
        )
      },
    },
  ]

  return (
    <CCardBody>
      {contextHolder}
      <CButton
        color="primary"
        className="mb-2"
        onClick={() => serviceSyncMounted()}
      >
        Sync
      </CButton>

      {/* ── Filters panel ─────────────────────────────────── */}
      <div
        style={{
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 12,
          border: '1px solid var(--crm-border, #d9d9d9)',
          background: 'var(--crm-bg, #fff)',
        }}
      >
        <Flex gap={10} wrap="wrap" align="center">
          <Input
            prefix={
              <SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />
            }
            placeholder="Service"
            value={inputService}
            onChange={(e) => setInputService(e.target.value)}
            onPressEnter={() => applyServiceSearch(inputService)}
            onBlur={() => applyServiceSearch(inputService)}
            allowClear
            onClear={clearServiceSearch}
            style={{ width: 220 }}
          />

          <RangePicker
            value={dateRange}
            onChange={(vals) => {
              if (vals && vals[0] && vals[1]) {
                setFilters({
                  updatedAt: [
                    vals[0].format('YYYY-MM-DD'),
                    vals[1].format('YYYY-MM-DD'),
                  ],
                })
              } else {
                setFilters({ updatedAt: undefined })
              }
              setPage(1)
            }}
            format="YYYY-MM-DD"
            placeholder={['Updated from', 'to']}
            style={{ width: 260 }}
          />
        </Flex>

        {activeTags.length > 0 && (
          <>
            <div
              style={{
                height: 1,
                background: 'var(--crm-border, #e2e5ec)',
                margin: '10px 0 8px',
              }}
            />
            <Flex gap={6} wrap="wrap" align="center">
              <Text
                type="secondary"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  whiteSpace: 'nowrap',
                }}
              >
                Active:
              </Text>
              {activeTags.map((t) => (
                <Tag
                  key={t.key}
                  closable
                  onClose={t.clear}
                  style={{ fontSize: 11, margin: 0 }}
                  color="blue"
                >
                  {t.label}
                </Tag>
              ))}
              <Button
                size="small"
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => {
                  resetFilters()
                  setInputService('')
                  setPage(1)
                }}
                style={{ color: '#ef4444', fontSize: 11 }}
              >
                Reset all
              </Button>
            </Flex>
          </>
        )}
      </div>

      <Table
        dataSource={serviceList}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="serviceListId"
        columns={columns}
        pagination={false}
        onChange={(pagination, _filters, sorter) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
          })
        }
      />
    </CCardBody>
  )
}
